import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase, getLocalUser } from '../supabase';
import { theme } from '../theme';
import { TagIcon } from '../Icons';
import { createBookingLink, verifyPrice, createFreeze, getFreezes, getPinPricePoints, reactivatePin, getTierLimits, startTrialWatch } from '../api/atlast';
import FreezePaymentModal from './FreezePaymentModal';
import FreezeDurationModal, { FREEZE_DURATION_TIERS } from './FreezeDurationModal';
import PurchaseSuccessModal from './PurchaseSuccessModal';
import PageBackground from './PageBackground';
import { formatTime, ItineraryLeg } from '../ItineraryLeg';
import { useDestinationPhoto } from '../useDestinationPhoto';
import ShareDeal from '../ShareDeal';
import TripExtras, { SectionLabel } from '../TripExtras';
import AppNav from '../AppNav';
import { pickRibbon, computeRoutePriceStats, formatCheckedWhen, formatMoney, cityFromAirportName, pinCity, friendlyDetail, recoverCreatedFreeze } from '../utils/dealExtras';
import { watchOutcome, OUTCOME_STYLE } from '../utils/fee';
import { getAltFare, canSwitchFare, altTitle } from '../utils/altFare';
import SwitchFareModal from './SwitchFareModal';

// Check-count history: went through two prior iterations (a dot strip,
// then a "jar of balls" visualization) before landing here (Aug 13) —
// "checks logged" now lives as a small badge directly on DealMiniChart's
// own header instead of being a separate widget, since a disconnected
// jar sitting in the center column read as unrelated to the price/
// itinerary above it. See DealMiniChart below for the actual display.

// useContainerWidth (Sep 23) — same small hook as FreezeDetailScreen.js's
// chart (kept as its own local copy rather than a shared import, to
// match how ItineraryLeg's formatTime/formatDate are the only things
// actually promoted to a shared file so far): reports the chart's real
// rendered width so DealMiniChart can pick its desktop/narrow layout
// the same way the Watch screen's chart does, instead of guessing from
// the viewport.
function useContainerWidth(ref) {
  const [width, setWidth] = useState(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const el = ref.current;
    const update = () => setWidth(el.getBoundingClientRect().width);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

// DealMiniChart (Sep 23, per Ali's report — visual-consistency pass):
// rebuilt to share the Watch screen's chart language (gridlines, axis
// labels, fonts, card framing, hover styling) instead of a separate,
// simpler look — same underlying /trips/{pin_id}/price-points data as
// before, no freeze needed. Deliberately keeps ONE difference: no
// threshold band, since a free pin has no $0 lines to show — a free
// pin isn't "before a watch," it's just tracked, so the whole line is
// drawn in the same solid navy the Watch chart uses for its "during
// your watch" segment, not the muted dashed "before" treatment.
// Handles the full realistic range of data availability honestly — a
// Deal-page pin has no minimum check-count guarantee (pinIsDeal() only
// checks current price vs. budget), so this can see anywhere from 0 to
// dozens of points.
function DealMiniChart({ points, checksLogged, currency, standalone }) {
  const wrapRef = useRef(null);
  const containerWidth = useContainerWidth(wrapRef);
  const narrow = containerWidth != null && containerWidth < 420;
  const [hover, setHover] = useState(null);
  // Same currency bug as the Watch screen's chart, fixed the same way
  // (Aug 14, per Ali's report — see
  // migrations/add_price_converted_to_price_check_log.sql): the raw
  // `price` column can be in a different currency check to check
  // whenever Duffel falls back to a different settlement currency than
  // requested, so this now prefers price_converted and drops any point
  // it can't trust, rather than plotting raw values as one number line.
  const resolveValue = (p) => {
    if (p.price_converted != null) return p.price_converted;
    if (p.currency && p.currency === currency) return p.price;
    return null;
  };
  const pts = (points || []).map(p => ({ t: new Date(p.checked_at).getTime(), price: resolveValue(p) })).filter(p => p.price != null);

  const eyebrow = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '4px' }}>
      <div>
        <div style={{ fontSize: '13px', fontWeight: 800, color: theme.structural }}>Your price, tracked</div>
        <div style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.4 }}>Real checks from our own tracking. We don't predict where the price goes.</div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
        {pts.length > 0 && (
          <span style={{ fontFamily: theme.fontMono, fontSize: '12px', fontWeight: 800, background: theme.structural, color: '#fff', padding: '5px 9px', whiteSpace: 'nowrap' }}>
            Now {formatMoney(pts[pts.length - 1].price)} {currency}
          </span>
        )}
        {checksLogged != null && (
          <span style={{ fontSize: '12.5px', fontWeight: 800, background: theme.action, color: theme.structural, padding: '5px 9px', whiteSpace: 'nowrap' }}>
            {checksLogged} check{checksLogged !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );

  if (pts.length === 0) {
    return (
      <div ref={wrapRef} style={standalone ? {} : { marginTop: '14px', paddingTop: '14px', borderTop: `2px solid ${theme.border}` }}>
        <style>{`@keyframes fd-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
        {eyebrow}
        <div style={{ textAlign: 'center', padding: '18px 8px', background: theme.bg, marginTop: '8px' }}>
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: theme.structural, margin: '0 auto 8px', animation: 'fd-pulse 1.5s infinite' }} />
          <div style={{ fontSize: '12.5px', fontWeight: 700, color: theme.textPrimary }}>Still gathering data...</div>
          <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '4px' }}>Your first price check will land here soon.</div>
        </div>
      </div>
    );
  }

  if (pts.length === 1) {
    return (
      <div ref={wrapRef} style={standalone ? {} : { marginTop: '14px', paddingTop: '14px', borderTop: `2px solid ${theme.border}` }}>
        {eyebrow}
        <div style={{ textAlign: 'center', padding: '14px 8px', background: theme.bg, marginTop: '8px' }}>
          <div style={{ fontFamily: theme.fontMono, fontSize: '22px', fontWeight: 800, color: theme.structural }}>{formatMoney(pts[0].price)} {currency}</div>
          <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '4px' }}>Just started tracking — not enough checks yet for a trend line.</div>
        </div>
      </div>
    );
  }

  // Sizing matches the Watch chart's own narrow/desktop split (same
  // breakpoint logic, smaller default height since this sits inside a
  // card rather than its own full section).
  const L = narrow
    ? { w: Math.max(280, Math.round(containerWidth)), h: 190, padL: 44, padR: 8, padTop: 10, padBottom: 26, fs: 11, fsS: 10.5 }
    : (standalone
        ? { w: Math.min(1100, Math.max(560, Math.round(containerWidth || 900))), h: 250, padL: 56, padR: 14, padTop: 14, padBottom: 32, fs: 12, fsS: 11.5 }
        : { w: 460, h: 230, padL: 50, padR: 12, padTop: 12, padBottom: 30, fs: 11.5, fsS: 11 });

  const domainStart = pts[0].t, domainEnd = pts[pts.length - 1].t;
  const domainSpan = Math.max(domainEnd - domainStart, 1);
  let yMin = Math.min(...pts.map(p => p.price)), yMax = Math.max(...pts.map(p => p.price));
  const ySpan = (yMax - yMin) || Math.max(1, Math.abs(yMin) * 0.01);
  yMin -= ySpan * 0.08; yMax += ySpan * 0.08;

  const plotL = L.padL, plotR = L.w - L.padR, plotT = L.padTop, plotB = L.h - L.padBottom;
  const xOf = (t) => plotL + ((t - domainStart) / domainSpan) * (plotR - plotL);
  const yOf = (price) => plotT + ((yMax - price) / (yMax - yMin)) * (plotB - plotT);
  const plotted = pts.map(p => ({ ...p, x: xOf(p.t), y: yOf(p.price) }));
  const linePoints = plotted.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  // Same 4-tick round-number gridline logic as FreezeFullChart.
  const rawStep = (yMax - yMin) / 3;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep || 1)));
  const step = [1, 2, 2.5, 5, 10].map(m => m * mag).find(s => s >= rawStep) || rawStep || 1;
  const yTicks = [];
  for (let v = Math.ceil(yMin / step) * step; v <= yMax; v += step) yTicks.push(v);

  const fmtShort = (t) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const fmtDateTime = (t) => new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  function handlePointer(clientX, target) {
    const rect = target.getBoundingClientRect();
    const svgX = ((clientX - rect.left) / rect.width) * L.w;
    let nearest = plotted[0], minDist = Infinity;
    for (const p of plotted) { const d = Math.abs(p.x - svgX); if (d < minDist) { minDist = d; nearest = p; } }
    setHover(nearest);
  }

  return (
    <div ref={wrapRef} style={standalone ? {} : { marginTop: '14px', paddingTop: '14px', borderTop: `2px solid ${theme.border}` }}>
      {eyebrow}
      {pts.length < 5 && (
        <div style={{ fontSize: '11.5px', color: theme.textMuted, fontStyle: 'italic', margin: '6px 0 2px' }}>
          Only {pts.length} checks so far — the picture will get clearer over the next few days.
        </div>
      )}
      <div style={{ position: 'relative', marginTop: '6px' }}>
        <svg width="100%" viewBox={`0 0 ${L.w} ${L.h}`} style={{ cursor: 'crosshair', display: 'block' }}
          onMouseMove={(e) => handlePointer(e.clientX, e.currentTarget)}
          onMouseLeave={() => setHover(null)}
          onTouchStart={(e) => handlePointer(e.touches[0].clientX, e.currentTarget)}
          onTouchMove={(e) => handlePointer(e.touches[0].clientX, e.currentTarget)}>
          {yTicks.map(v => (
            <g key={v}>
              <line x1={plotL} y1={yOf(v)} x2={plotR} y2={yOf(v)} stroke="#E4E9ED" strokeWidth="1" />
              <text x={plotL - 8} y={yOf(v) + 3} textAnchor="end" fontSize={L.fsS} fill={theme.textMuted}>{formatMoney(v)}</text>
            </g>
          ))}
          <polyline points={linePoints} fill="none" stroke={theme.structural} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          {/* Same "few dots" rule as the Watch chart: at most 5 evenly
              spaced check dots, never one per check. */}
          {plotted.slice(0, -1).filter((_, i, a) => a.length <= 4 || i % Math.ceil(a.length / 4) === 0).map(p => (
            <circle key={p.t} cx={p.x} cy={p.y} r="3.5" fill={theme.structural} />
          ))}
          {plotted.length > 0 && (
            <circle cx={plotted[plotted.length - 1].x} cy={plotted[plotted.length - 1].y} r="6" fill="#F4C11C" stroke="#1E3A5F" strokeWidth="2.5" />
          )}
          {hover && (
            <>
              <line x1={hover.x} y1={plotT} x2={hover.x} y2={plotB} stroke={theme.action} strokeWidth="1.5" opacity="0.6" />
              <circle cx={hover.x} cy={hover.y} r="5" fill={theme.action} stroke="#fff" strokeWidth="1.5" />
            </>
          )}
          <text x={plotL} y={L.h - 8} fontSize={L.fsS} fill={theme.textMuted}>{fmtShort(domainStart)}</text>
          <text x={plotR} y={L.h - 8} textAnchor="end" fontSize={L.fsS} fontWeight="700" fill={theme.structural}>{fmtShort(domainEnd)}</text>
          <line x1={plotL} y1={plotB} x2={plotR} y2={plotB} stroke="#C3CFDA" strokeWidth="1" />
        </svg>
        {hover && (
          <div style={{
            position: 'absolute', pointerEvents: 'none',
            left: `${Math.min(Math.max((hover.x / L.w) * 100, 18), 82)}%`, top: `${(hover.y / L.h) * 100}%`,
            transform: 'translate(-50%, calc(-100% - 10px))', background: theme.structural, color: '#fff',
            padding: '6px 9px', fontSize: '12px', lineHeight: 1.4, whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
          }}>
            <div style={{ color: '#C3CFDA', fontSize: '11.5px' }}>{fmtDateTime(hover.t)}</div>
            <div style={{ fontFamily: theme.fontMono, fontWeight: 800 }}>{formatMoney(hover.price)} {currency}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// FreezeChecklist — simplified (Aug 31, per Ali's report — Phase 4 of
// the new pin model). Was a 2-step progress checklist (Marked Urgent +
// 10 price checks) gating Price Freeze eligibility; both conditions
// are gone entirely under the new model, where pin_is_deal is the ONLY
// eligibility rule (see routes/freezes.py). This component already
// only ever rendered inside DealScreen's `{bestDeal && (...)}` block,
// which structurally guarantees "this pin is currently a deal" is
// true anywhere this shows (see the removed comment this replaces,
// which already noted that same guarantee for the old "currently a
// deal" checklist row) — so the "not ready yet" branch that used to
// exist here is now permanently unreachable, not just unlikely, and is
// removed rather than left as dead code, unlike the smaller harmless
// dead-code left elsewhere tonight (e.g. trips.py's toggle_urgent) —
// this one was a whole extra render branch, worth actually deleting.
// Rotating openers (Sep 23, per Ali's report) — a relatable reason to
// wait, since "not ready to book" read as generic. Picked once per pin
// (hashed from the pin's own id, not random-per-render), so the same
// pin always shows the same line and it only changes when someone pins
// a genuinely different trip — a line that changed on every refresh
// would read as broken, not personal.
const FREEZE_PITCH_OPENERS = [
  "Found it, but need to check with your spouse?",
  "Found it, but need the boss to sign off?",
  "Found it, but need a day to think it over?",
  "Found it, but not the right moment yet?",
  "Found it, but need to run it by someone first?",
  "Found it, but waiting on the right payday?",
];

function pitchOpenerForPin(pinId) {
  const s = String(pinId || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return FREEZE_PITCH_OPENERS[h % FREEZE_PITCH_OPENERS.length];
}

// FreezeChecklist — rebuilt as the "crisp two-chip" pitch card (Sep 23,
// per Ali's approved mockup), replacing the earlier per-duration
// "What you'll pay" table version. That table tried to do two jobs at
// once — sell the idea, AND specify one duration's exact numbers
// nobody had picked yet — which is why it read as both vague (no
// stated duration) and overloaded (a six-row table before any choice
// was made). This card now does ONLY the selling job: both tiers
// visible as plain US$ chips, the actual $5/$20 result rule stated in
// plain language, and a single footnote deferring exact local-currency
// amounts to the next screen (FreezeDurationModal), which is where a
// real per-duration quote belongs once the user has actually picked
// one. No network call here anymore — the earlier version's
// getWatchQuote fetch existed only to price this card correctly, and
// this design doesn't quote a specific number here at all.
function FreezeChecklist({ freezeStatus, freezeError, onFreeze, pin, trialAvailable, trialStatus, trialError, onStartTrial }) {
  const headline = `${pitchOpenerForPin(pin.id)} We'll watch it for you.`;

  return (
    <div style={{ background: theme.structural, borderRadius: '14px', padding: '28px', color: '#fff', display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: '5px', background: 'rgba(244,193,28,0.18)', color: '#F4C11C', fontFamily: theme.fontMono, letterSpacing: '0.08em', fontSize: '10.5px', fontWeight: 800, padding: '5px 12px', borderRadius: '99px', marginBottom: '12px' }}>
        PRICE WATCH
      </div>

      {/* 2-line safety net (Sep 23) — the six openers vary a lot in
          length ("not the right moment yet?" vs. "need to check with
          your spouse?"), and the mockup's font is only a close
          approximation of the app's real one, so a phrase that just
          fits in the mockup could wrap to 3 lines on a real device.
          line-clamp caps every opener at 2 lines with an ellipsis
          rather than letting a long one push the chips down
          unpredictably. */}
      <h3 style={{
        margin: '0 0 6px', fontSize: '19px', fontWeight: 800, lineHeight: 1.3,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
      }}>
        {headline}
      </h3>
      <p style={{ margin: '0 0 20px', fontSize: '12.5px', color: 'rgba(255,255,255,0.82)', lineHeight: 1.5 }}>
        Checked every 2 hours. You pay nothing if it ends US$5<sup style={{ fontSize: '0.75em', fontWeight: 800 }}>*</sup> or more above, or US$20<sup style={{ fontSize: '0.75em', fontWeight: 800 }}>*</sup> or more below, where it started.
      </p>

      {/* First-timer free trial (Sep 24) — the primary CTA whenever
          trialAvailable is true, per the approved mockup: leads with
          the free 24h trial, paid tiers demoted underneath as "or go
          straight to a longer watch." Once trialAvailable is false
          (this account has started any watch, free or paid), this
          whole block disappears and the paid chips below become the
          only, full-weight option again — no separate "used" state to
          maintain, since trialAvailable already encodes it. */}
      {trialAvailable && (
        <div style={{ border: '2px solid #F4C11C', background: 'rgba(244,193,28,0.1)', padding: '18px 20px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <span style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 800, letterSpacing: '0.05em', color: theme.action }}>FIRST ONE'S FREE</span>
          <div style={{ fontSize: '17px', fontWeight: 800, color: '#fff' }}>Try Price Watch for 24 hours</div>
          <p style={{ margin: 0, fontSize: '12.5px', color: 'rgba(255,255,255,0.78)', lineHeight: 1.5 }}>
            Checked every 2 hours, same as always. No card needed and nothing to pay. One trial per account.
          </p>
          <div
            onClick={trialStatus === 'starting' ? undefined : onStartTrial}
            style={{ cursor: trialStatus === 'starting' ? 'default' : 'pointer', opacity: trialStatus === 'starting' ? 0.7 : 1 }}
          >
            <div style={{ textAlign: 'center', background: theme.action, color: '#1a1a1a', border: 'none', padding: '13px 0', fontSize: '14px', fontWeight: 800 }}>
              {trialStatus === 'starting' ? 'Starting...' : 'Start my free watch →'}
            </div>
            {trialStatus === 'error' && trialError && (
              <p style={{ fontSize: '12px', color: '#ffd6d0', marginTop: '8px', fontWeight: 600, textAlign: 'center' }}>{trialError}</p>
            )}
          </div>
        </div>
      )}

      {trialAvailable && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          <span style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.25)' }} />
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>or go straight to a longer watch</span>
          <span style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.25)' }} />
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', opacity: trialAvailable ? 0.85 : 1 }}>
        {FREEZE_DURATION_TIERS.map(tier => (
          <div key={tier.hours} style={{
            flex: 1, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '3px',
            border: `1.5px solid ${!trialAvailable && tier.hours === 120 ? theme.action : 'rgba(255,255,255,0.4)'}`
          }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: !trialAvailable && tier.hours === 120 ? theme.action : 'rgba(255,255,255,0.75)' }}>{tier.label}</span>
            <span style={{ fontFamily: theme.fontMono, fontSize: '20px', fontWeight: 800 }}>
              US${tier.fee.toFixed(2)}<sup style={{ fontSize: '0.5em', fontWeight: 800 }}>*</sup>
            </span>
          </div>
        ))}
      </div>

      <p style={{ margin: '0 0 20px', fontSize: '12px', color: 'rgba(255,255,255,0.62)', lineHeight: 1.5 }}>
        * All US$ amounts shown in your own currency, based on the price when you start watching — you'll see the exact amount on the next screen, before you pay anything.
      </p>

      <div
        onClick={freezeStatus === 'freezing' ? undefined : onFreeze}
        style={{ cursor: freezeStatus === 'freezing' ? 'default' : 'pointer', opacity: freezeStatus === 'freezing' ? 0.7 : 1 }}
      >
        <div style={{
          textAlign: 'center', background: 'transparent', color: '#fff',
          border: '1.5px solid rgba(255,255,255,0.5)', padding: '11.5px 0', borderRadius: '14px',
          fontSize: '13px', fontWeight: 700
        }}>
          {freezeStatus === 'freezing' ? 'Starting… this can take about 10 seconds' : 'See your options →'}
        </div>
        {freezeStatus === 'error' && freezeError && (
          <p style={{ fontSize: '12px', color: '#ffd6d0', marginTop: '8px', fontWeight: 600, textAlign: 'center' }}>{freezeError}</p>
        )}
      </div>
    </div>
  );
}
// FreezeExplainerCard removed entirely (Aug 17, per Ali's report — the
// checklist/explainer redesign). It used to render as a second, full
// card stacked below FreezeChecklist for the not-ready state — its own
// 3-row breakdown plus two outcome boxes, on top of the checklist's own
// content, was the actual source of the "too much reading before any
// action" problem flagged directly from a live screenshot. That
// content didn't move elsewhere; it was compressed to one line inside
// FreezeChecklist itself (see its own "Once ready:" line) — the full
// outcome breakdown still exists, just at the ready state where it's
// actually the decision moment, not repeated during the wait.

// Module-level formatTime and ItineraryLeg now live in
// ../ItineraryLeg.js — shared with FreezeDetailScreen.js instead of
// duplicated per-file. That duplication was exactly what caused a real
// production crash earlier ("formatTime is not defined"), so this is
// one source of truth now, not a per-file convention to keep in sync.

function DealScreen() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const pin = state?.pin;
  const [bookingLoading, setBookingLoading] = useState(false);
  // Inactive-pin reactivation state (Sep 15, per Ali's report) —
  // declared here, well before the `if (!pin)` early return below,
  // since hooks can't sit after a conditional return. See the
  // showInactiveNotice/handleReactivateFromDeal block further down
  // (right by the freshness indicator) for how these actually get used.
  const [justReactivated, setJustReactivated] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [reactivateError, setReactivateError] = useState(null);
  const [verifyingPrice, setVerifyingPrice] = useState(false);
  const [priceCheck, setPriceCheck] = useState(null); // { status, previous_price, current_price, changed, direction }
  const [showBookingLinks, setShowBookingLinks] = useState(false);
  const [itinOpen, setItinOpen] = useState(true);
  // "Track this fare instead" (Oct 2026)
  const [altOpen, setAltOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  // 'idle' | 'freezing' | 'active' | 'error' — tracks the Price Freeze
  // button's own state, separate from booking/verification above.
  const [freezeStatus, setFreezeStatus] = useState('idle');
  const [freezeError, setFreezeError] = useState(null);
  // Duration tier selection (Sep 8, per Ali's report) — defaults to
  // the original 5-day (120h) tier, matching the backend's own
  // default so an untouched selection behaves exactly like the
  // product did before this shipped. Lives here, not in
  // FreezeChecklist, since handleFreeze (which needs to read it when
  // calling createFreeze) lives in this component too.
  const [selectedDurationHours, setSelectedDurationHours] = useState(120);
  // The quote FreezeDurationModal fetched for the chosen tier, carried
  // forward into FreezePaymentModal rather than re-fetched there (Sep
  // 23) — guarantees both screens show the exact same converted
  // numbers even if the stored FX rate happens to refresh in the few
  // seconds between picking a duration and confirming payment.
  const [selectedQuote, setSelectedQuote] = useState(null);
  // Payment modal (Aug 14, per Ali's report — Stripe wiring): the
  // checklist's CTA no longer calls handleFreeze directly, it opens
  // this modal first (see the onFreeze prop passed to FreezeChecklist
  // below). The modal itself decides whether a card actually needs
  // collecting at all — if Stripe isn't configured yet, or this user
  // already has a saved+consented card, it calls handleFreeze
  // immediately with no payment_method_id needed, which hits the same
  // path create_freeze already supports either way.
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  // Duration-picker step (Sep 8, per Ali's report — real flow
  // correction: clicking "Freeze this price" now opens THIS first,
  // not the payment modal directly. Picking a tier here is what
  // actually transitions to FreezePaymentModal, via
  // handleDurationChosen below — a genuine two-step flow, not
  // duration selection folded into the pitch card itself.
  const [showDurationModal, setShowDurationModal] = useState(false);
  // Fixes the actual redundancy bug flagged from a live screenshot: this
  // pin might already have an active freeze from a previous visit (this
  // component's own freezeStatus only ever reflects the CURRENT session's
  // attempt, and handleFreeze navigates away immediately on success, so
  // it was never a reliable signal for "does this pin already have one").
  // Fetching the real freeze list here is the only way to know for sure.
  const [activeFreeze, setActiveFreeze] = useState(null);
  // Real gap found live (Sep 4, per Ali's report) — same one just
  // fixed on the Dashboard: once a freeze resolves, this page fell
  // straight back to a plain "Freeze this price" checklist with zero
  // memory of the outcome. Tracks the most recent resolved freeze for
  // this specific pin so it can say so, persisting indefinitely.
  const [resolvedFreeze, setResolvedFreeze] = useState(null);
  // Any Price Watch on this trip, ever (not just the latest resolved one). The
  // alternative-fare card waits for this to load so it never flashes and vanishes.
  const [freezesLoaded, setFreezesLoaded] = useState(false);
  const [hasFreezeHistory, setHasFreezeHistory] = useState(false);

  // Feeds DealMiniChart below — no freeze required, works for any pin
  // that's been checked at all. Best-effort: a failed fetch just means
  // the chart shows its own zero-points fallback state, never blocks
  // the rest of the page.
  const [pricePoints, setPricePoints] = useState([]);

  // Destination photo (Aug 16, per Ali's report — design item 4 UI
  // wiring). Called unconditionally here, before any early returns
  // below, same as every other hook in this component — pin?.destination
  // being undefined on first render (before pin loads) is handled
  // inside the hook itself, not by skipping the call.
  const heroPhotoUrl = useDestinationPhoto(pin?.destination);

  useEffect(() => {
    if (!pin?.user_id || !pin?.id) return;
    let cancelled = false;
    getFreezes(pin.user_id).then(res => {
      if (cancelled) return;
      const pinFreezes = (res.freezes || []).filter(f => f.pin_id === pin.id);
      const match = pinFreezes.find(f => f.status === 'active');
      setActiveFreeze(match || null);
      const resolved = pinFreezes
        .filter(f => (f.status === 'kept' || f.status === 'refunded') && f.resolved_at)
        .sort((a, b) => new Date(b.resolved_at) - new Date(a.resolved_at))[0];
      setResolvedFreeze(resolved || null);
      setHasFreezeHistory(pinFreezes.length > 0);
      setFreezesLoaded(true);
    }).catch(() => null);
    return () => { cancelled = true; };
  }, [pin?.user_id, pin?.id]);

  // Free trial eligibility (Sep 24) — piggybacked onto the existing
  // tier-limits response (see routes/trips.py's get_limits), same
  // call Dashboard.js already makes once per load, not a new
  // endpoint. Fails to false (trial button hidden) on any error, same
  // "fail toward not showing the feature" reasoning as elsewhere.
  const [trialAvailable, setTrialAvailable] = useState(false);
  useEffect(() => {
    if (!pin?.user_id) return;
    let cancelled = false;
    getTierLimits(pin.user_id).then(res => {
      if (!cancelled) setTrialAvailable(!!res.price_watch_trial_available);
    }).catch(() => { if (!cancelled) setTrialAvailable(false); });
    return () => { cancelled = true; };
  }, [pin?.user_id]);

  useEffect(() => {
    if (!pin?.id) return;
    let cancelled = false;
    getPinPricePoints(pin.id).then(res => {
      if (!cancelled) setPricePoints(res.points || []);
    }).catch(() => null);
    return () => { cancelled = true; };
  }, [pin?.id]);

  // Real explanatory confirmation added (Sep 3, per Ali's report)
  // between a successful charge and navigating away — previously this
  // went straight to /freeze with no confirmation shown at all.
  // Navigation itself is deliberately held until "Got it" is clicked
  // (pendingFreezeNav), not fired immediately alongside the modal —
  // the whole point is a person actually reads what just happened
  // before landing on the next screen.
  const [pendingFreezeNav, setPendingFreezeNav] = useState(null);

  // Duration chosen (Sep 8, per Ali's report) — the actual transition
  // point between the two steps: closes the duration picker, records
  // the choice, then opens the real payment modal. handleFreeze itself
  // already reads selectedDurationHours when it calls createFreeze, so
  // nothing else needs to change downstream — this is purely the
  // hand-off between step one and step two.
  const handleDurationChosen = (hours, quote) => {
    setSelectedDurationHours(hours);
    setSelectedQuote(quote);
    setShowDurationModal(false);
    setShowPaymentModal(true);
  };

  const handleFreeze = async (paymentMethodId = null, saveCard = false) => {
    console.log('[DIAG] DealScreen handleFreeze called');
    setShowPaymentModal(false);
    setFreezeStatus('freezing');
    setFreezeError(null);
    const startedAt = Date.now();
    try {
      const result = await createFreeze(pin.id, pin.user_id, paymentMethodId, saveCard, selectedDurationHours);
      console.log('[DIAG] DealScreen createFreeze succeeded, result:', result);
      // justCreated: true gates the one-time celebration header (shimmer +
      // "Price frozen!" pill) — a revisit via FreezeTracker's card click
      // should never re-trigger it, so only the two "just paid" entry
      // points (here and Dashboard's button) set this flag. fromDeal +
      // pin let FreezeDetailScreen offer a real "back to this deal" path
      // instead of only ever pointing back to the Dashboard.
      setPendingFreezeNav({ freezeId: result.freeze.id, userId: pin.user_id, justCreated: true, fromDeal: true, pin });
      console.log('[DIAG] DealScreen setPendingFreezeNav called');
    } catch (error) {
      console.error('Freeze request failed:', error);
      // No answer from the server at all (dropped/slow connection): the watch
      // may have been created anyway, so check before showing an error.
      if (!error?.response) {
        const made = await recoverCreatedFreeze(pin.id, pin.user_id, startedAt);
        if (made) {
          setPendingFreezeNav({ freezeId: made.id, userId: pin.user_id, justCreated: true, fromDeal: true, pin });
          return;
        }
      }
      setFreezeStatus('error');
      setFreezeError(friendlyDetail(error?.response?.data?.detail) || 'We couldn\'t confirm your Price Watch. Check your connection, then look for it on your Dashboard before trying again.');
    }
  };

  // Starts the free trial directly (Sep 24) — no duration picker (it's
  // always 24h) and no payment modal (nothing to pay), so this skips
  // straight from the pitch card's trial button to a real freeze,
  // unlike handleFreeze's two-step flow above. Also skips the
  // PurchaseSuccessModal/pendingFreezeNav confirmation step those use:
  // that modal shows a fee/duration summary from FREEZE_DURATION_TIERS,
  // which has no 24h entry and nothing to confirm paying anyway —
  // asking someone to click "Got it" on a payment summary for a free
  // action would be pure friction, not real confirmation of anything.
  const [trialStatus, setTrialStatus] = useState('idle');
  const [trialError, setTrialError] = useState(null);
  const handleStartTrial = async () => {
    setTrialStatus('starting');
    setTrialError(null);
    try {
      const result = await startTrialWatch(pin.id, pin.user_id);
      navigate('/freeze', { state: { freezeId: result.freeze.id, userId: pin.user_id, justCreated: true, fromDeal: true, pin } });
    } catch (error) {
      console.error('Trial watch request failed:', error);
      setTrialStatus('error');
      setTrialError(friendlyDetail(error?.response?.data?.detail) || 'Could not start the free trial. Try again.');
    }
  };

  // Same trust computation as Dashboard.js's renderTrustDetails — kept
  // here too since the deal screen previously showed none of this.
  // "Lowest price yet" requires 3+ history points so it's not trivially
  // true on someone's first or second check.
  const priceHistory = pin?.price_history || [];
  const isLowestYet = priceHistory.length >= 3 && pin?.last_price != null
    && pin.last_price <= Math.min(...priceHistory.map(h => h.price));

  // Same fix as Dashboard.js — send people to the marketing site rather
  // than back to this app's own login screen. flyatlast.com is a
  // separate domain, so this needs a real browser redirect.
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = 'https://flyatlast.com';
  };

  // Shared trip params every deep-link builder below needs. Pulled out
  // once so the three builders can't silently drift from each other on
  // which date field or passenger shape they read.
  const getLinkParams = () => ({
    origin: pin.origin_code.toLowerCase(),
    destination: pin.destination_code.toLowerCase(),
    // matched_departure_date/matched_return_date (the exact date the shown
    // price was actually found on) rather than the pin's original anchor
    // date — a flexible pin's cheapest match at, say, Aug 12 should send
    // the user to search Aug 12, not the original Aug 15 anchor.
    departureDate: pin.matched_departure_date || pin.departure_date,
    returnDate: pin.matched_return_date || pin.return_date,
    adults: pin.adults || 1,
    childAges: pin.child_ages || [],
    // The pin's own budget currency — used to force Skyscanner/Booking.com
    // to display results in the same currency we showed on this screen,
    // rather than letting them silently apply their own geo-IP-detected
    // default. Without this, a visitor whose location differs from their
    // saved budget currency (e.g. traveling abroad) would see one currency
    // here and a different one after clicking through — undermining the
    // whole point of the FX-conversion work already done on this screen.
    currency: pin.currency || 'CAD'
  });

  const buildSkyscannerUrl = () => {
    const { origin, destination, departureDate, returnDate, adults, childAges, currency } = getLinkParams();
    let url = `https://www.skyscanner.com/transport/flights/${origin}/${destination}/`;

    if (departureDate) {
      // Skyscanner's date path segment format: YYMMDD
      const toSkyscannerDate = (isoDate) => {
        const [y, m, d] = isoDate.split('-');
        return `${y.slice(2)}${m}${d}`;
      };
      url += `${toSkyscannerDate(departureDate)}/`;
      if (returnDate) url += `${toSkyscannerDate(returnDate)}/`;
    }

    // IMPORTANT: the live consumer page at www.skyscanner.com/transport/flights/
    // reads `adultsv2` / `childrenv2`, NOT the legacy `adults` / `children`
    // params — sending the legacy names causes Skyscanner to silently fall
    // back to 1 adult, Economy, even though the params are visibly present.
    const params = new URLSearchParams();
    params.set('adultsv2', adults);

    if (childAges.length > 0) {
      // Skyscanner's children param only supports ages 2-17. True infants
      // (0-1) aren't representable here even though Duffel priced them
      // correctly via exact age — filtered out rather than sent as an
      // invalid value Skyscanner might reject or misinterpret.
      const compatibleAges = childAges.filter(age => age >= 2 && age <= 17);
      params.set('childrenv2', compatibleAges.length > 0 ? compatibleAges.join('|') : '');
    }

    // Forces the results to display in the pin's budget currency instead
    // of Skyscanner's own geo-IP-detected default — documented as a
    // supported param on Skyscanner's referral endpoints (day-view,
    // multicity); carrying it over to this consumer URL is expected to
    // work the same way since it's the same underlying results page, but
    // worth confirming with a live check after deploying, same as the
    // adultsv2/childrenv2 params above were confirmed.
    params.set('currency', currency);

    return url + `?${params.toString()}`;
  };

  const buildBookingComUrl = () => {
    const { origin, destination, departureDate, returnDate, adults, childAges, currency } = getLinkParams();
    // IMPORTANT: www.booking.com/flights/index.html?from=...&to=... only
    // lands on the generic search form with fields pre-filled — it does
    // NOT auto-run the search, unlike Skyscanner.
    // The actual auto-search results endpoint lives on a different
    // subdomain with a path-based route (origin/destination as
    // {CODE}.AIRPORT / {CODE}.CITY segments), e.g.:
    //   flights.booking.com/flights/YYZ.AIRPORT-OSL.CITY/?type=ROUNDTRIP&...
    // Origin is always tagged .AIRPORT (we only have an airport code to
    // give it); destination uses .CITY, matching Booking.com's own
    // generated links for an airport-to-city flight search.
    const originCode = origin.toUpperCase();
    const destCode = destination.toUpperCase();
    const path = `${originCode}.AIRPORT-${destCode}.CITY`;

    const params = new URLSearchParams();
    params.set('type', returnDate ? 'ROUNDTRIP' : 'ONEWAY');
    params.set('adults', adults);
    params.set('cabinClass', 'ECONOMY');
    if (childAges.length > 0) {
      // Booking.com's captured URL format passes a single `children` value
      // rather than repeated params — matches the shape seen in their own
      // generated links (e.g. children=16 for one 16-year-old). For
      // multiple children this is a best-effort join; Booking.com's own
      // UI can be used to adjust once on the results page if needed.
      params.set('children', childAges.join(','));
    }
    params.set('from', `${originCode}.AIRPORT`);
    params.set('to', `${destCode}.CITY`);
    params.set('depart', departureDate);
    if (returnDate) params.set('return', returnDate);
    // Forces results into the pin's budget currency rather than
    // Booking.com's own default — confirmed via their actual flight
    // search backend shape that the correct parameter name is
    // `currency_code` (default "USD" when omitted), NOT `currency`.
    // The wrong name was likely why this looked like it "worked" on one
    // pin and not another: Booking.com was silently ignoring the
    // (incorrectly named) param both times and just happened to default
    // to CAD once, coincidentally matching that pin's budget currency.
    params.set('currency_code', currency);

    return `https://flights.booking.com/flights/${path}/?${params.toString()}`;
  };

  // handleBook removed (Aug 13) — its last remaining caller was the
  // "Skip re-verify, open Skyscanner directly" button inside "More ways
  // to book", itself removed per Ali's explicit call. buildSkyscannerUrl
  // itself is still used elsewhere (the primary Skyscanner button).

  // Primary "Book Now" flow: re-verify the price against Duffel right now
  // (rather than trusting whatever price_checker last saved, which could
  // be stale), show the user how that compares to what's on screen, then
  // reveal two pre-filled search links (Skyscanner, Booking.com) instead
  // of guessing which one to open automatically.
  //
  // Important limitation, stated plainly rather than glossed over: neither
  // of these two platforms offer a public way to link to one exact
  // fare/flight. Re-verifying via Duffel confirms whether OUR price moved;
  // it does not and cannot confirm what any of the three platforms will
  // show, since that's an independent live search on each of their sides.
  const handleVerifyAndBook = async (deal) => {
    setVerifyingPrice(true);
    setPriceCheck(null);
    try {
      const { departureDate, returnDate, adults, childAges } = getLinkParams();
      // deal.type is a human-readable display label ("Direct Flight",
      // "With Stops", "Best Value") used for on-screen text elsewhere on
      // this page — it never matched the backend's actual dict keys
      // ("direct", "with_stops", "best_value"), so deals.get(req.deal_type)
      // in verify_price has been returning None on every single request
      // ever made, unconditionally falling back to "unverified" — a real
      // bug found via two live tests both hitting the fallback message
      // regardless of pin. Translating to the real key here instead of
      // sending the display label directly.
      const DEAL_TYPE_TO_BACKEND_KEY = {
        'Direct Flight': 'direct',
        'With Stops': 'with_stops',
        'Best Value': 'best_value',
      };
      const result = await verifyPrice({
        origin: pin.origin_code,
        destination: pin.destination_code,
        departure_date: departureDate,
        return_date: returnDate || null,
        adults,
        child_ages: childAges,
        budget: pin.budget,
        currency: pin.currency || 'CAD',
        deal_type: DEAL_TYPE_TO_BACKEND_KEY[deal.type] || deal.type,
        previous_price: deal.price
      });
      setPriceCheck(result);
    } catch (err) {
      console.error('Price re-verification failed:', err);
      setPriceCheck({ status: 'unverified', previous_price: deal.price });
    } finally {
      setVerifyingPrice(false);
      setShowBookingLinks(true);
    }
  };

  // Builds a plain web search for the specific flight Duffel found —
  // reliably constructible for any airline (unlike a deep link into an
  // airline's own site, which has no universal URL format), and far more
  // likely to land on the actual flight than a blind Skyscanner re-search.
  const handleLookup = (flight) => {
    const depDate = new Date(flight.departure_time);
    const dateStr = depDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    const query = `${flight.airline} flight ${flight.flight_number} ${dateStr}`;
    window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, '_blank');
  };

  // DORMANT as of July 2026 — Duffel Links returns a 403
  // unavailable_feature_error on our account (feature is gated, Duffel
  // support unresponsive). Not wired to any button right now; the
  // primary CTA below uses handleVerifyAndBook() (re-verify, then show
  // Skyscanner/Booking.com/Google Flights) instead.
  // Left in place, not deleted, because the backend /links/create-session
  // route and its order.created webhook handling are correct and will
  // work as-is the moment Duffel enables Links on our account — just
  // repoint the primary button's onClick back to handleBookNow then.
  //
  // Opens a Duffel-hosted booking checkout (search + fare selection +
  // payment + e-ticket, branded to us) — genuinely different from
  // handleLookup above, which just opens a Google search. Important to
  // be upfront: this is a fresh search on Duffel's own inventory, not a
  // guaranteed re-purchase of the exact prior offer (Links doesn't
  // support pre-selecting a specific offer_id) — but it's the same
  // trusted source our price-checker already searches, with our markup
  // applied, and Duffel (not us) handles payment/PCI.
  // eslint-disable-next-line no-unused-vars
  const handleBookNow = async () => {
    setBookingLoading(true);
    try {
      const { data: { user } } = await getLocalUser();
      const result = await createBookingLink(user.id, pin.id);
      if (result?.url) {
        window.open(result.url, '_blank');
      } else {
        alert('Could not start checkout. Please try again.');
      }
    } catch (error) {
      alert(error?.response?.data?.detail || 'Could not start checkout. Please try again.');
      console.error('Error creating booking session:', error);
    }
    setBookingLoading(false);
  };

  if (!pin) {
    navigate('/dashboard');
    return null;
  }

  // Same freshness logic as Dashboard.js — Duffel offers expire ~15-30
  // min after being returned, so this is the most important place in the
  // app to flag staleness: right before the user clicks through expecting
  // this exact price to still be bookable.
  //
  // Shows the actual time the check ran (not "X min ago") — a clock time
  // stays meaningful however long it's been, where a relative label goes
  // stale-feeling itself the moment you glance away and back. The
  // underlying staleness thresholds (color, bold, verify-before-booking
  // warning) are unchanged, only the displayed label text changed.
  const getFreshnessState = (timestamp) => {
    if (!timestamp) return { label: 'Checking...', color: theme.textMuted, warn: false, live: false };

    const minutes = Math.floor((new Date() - new Date(timestamp)) / 60000);
    // Now includes the date, not just the time (Sep 15, per Ali's
    // report — see formatCheckedWhen's own comment in dealExtras.js
    // for the full reasoning: a bare time with no date was genuinely
    // ambiguous for anything older than today, and didn't match
    // Dashboard's own date-only "Found Sep 15" badge for the exact
    // same timestamp).
    const checkedStr = formatCheckedWhen(timestamp);

    if (minutes < 15) {
      return { label: `Checked ${checkedStr}`, color: theme.freshnessOk, warn: false, live: true };
    } else if (minutes < 30) {
      return { label: `Checked ${checkedStr}`, color: theme.freshnessAging, warn: false, live: false };
    } else {
      // Softened (Aug 17, per Ali's report — flagged directly from a
      // live screenshot): warn stays true — it's still a real signal
      // used elsewhere to suppress an exciting ribbon on stale data
      // (see the ribbon-suppression check below) and to show a plainer
      // "verify" note in the details panel, both legitimate. What
      // changed is the HERO's own rendering of this state (see its
      // render site) — it used to escalate to red/bold/a ⚠ triangle,
      // the same visual treatment as an actual error elsewhere in the
      // app. A check running a bit longer than usual is routine, not
      // a problem; dressing it up as one made a normal page load look
      // like something had gone wrong before anyone had even looked
      // at the price.
      return { label: `Checked ${checkedStr} — price may have moved since`, color: theme.freshnessAging, warn: true, live: false };
    }
  };

  const freshness = getFreshnessState(pin.last_checked);

  // Inactive-pin notice (Sep 15, per Ali's report — real gap: this
  // screen had zero awareness of is_active at all, so a paused,
  // checks-exhausted, or expired pin showed the exact same generic
  // "prices are dynamic" freshness copy as a pin still being actively
  // checked right now — no indication this specific price will never
  // update again without reactivating). `justReactivated` is
  // local-only since `pin` here is a navigation-state snapshot, not
  // live component state — reactivating doesn't refetch it, so this
  // flag is what actually flips the UI once the call succeeds. The
  // three hooks themselves live above, before the `if (!pin)` early
  // return, since hooks can't sit after a conditional return.
  const showInactiveNotice = pin.is_active === false && !justReactivated;

  const handleReactivateFromDeal = async () => {
    setReactivating(true);
    setReactivateError(null);
    try {
      await reactivatePin(pin.id);
      setJustReactivated(true);
    } catch (error) {
      const rawMessage = error?.response?.data?.detail || 'Something went wrong. Try again.';
      setReactivateError(rawMessage.startsWith('PIN_LIMIT_REACHED:') ? rawMessage.replace('PIN_LIMIT_REACHED:', '').trim() : rawMessage);
    } finally {
      setReactivating(false);
    }
  };

  // Each deal now carries its own `flight` detail (airline, flight
  // number, times, layover) alongside price — populated by
  // categorize_offers()/extract_itinerary() in the backend, stored on
  // the pin as direct_flight / stops_flight / best_value_flight.
  //
  // Which entry gets the badge (becoming `bestDeal`, the hero shown on
  // this page and the thing Book Now/Price Freeze act on) now follows
  // the pin's locked-at-creation flight_preference, instead of always
  // defaulting to "Best Value" regardless of what the pin owner chose
  // — that was the exact bug behind "I froze a price, went to check a
  // direct flight, and it was more expensive": the page itself was
  // always showing (and freezing) the cheapest-of-any-itinerary price,
  // never the nonstop one, even for someone who'd said they only
  // wanted nonstop.
  const isDirectPreference = pin.flight_preference === 'direct';

  // Real bug found from a live GBP pin: this filter used to compare
  // RAW prices (whatever currency Duffel actually returned — often a
  // fallback currency like USD, not the pin's budget currency) directly
  // against pin.budget, with zero conversion. Dashboard.js's
  // comparablePrice() already got fixed for this exact scenario earlier
  // today; this file's own separate deals-array filter never did,
  // which is why a pin Dashboard correctly shows as a deal could still
  // show nothing at all here. direct_price/stops_price don't have their
  // own server-computed *_converted siblings (only best_value_price/
  // last_price does, via last_price_converted) — rather than leaving
  // those two unconverted, apply the SAME real FX ratio Duffel/the
  // backend already computed for best_value, since it's the same
  // currency pair on the same check.
  // Real bug found (Sep 23, per Ali's report — a direct-preference
  // pin that Dashboard correctly showed as deal-ready crashed this
  // page to a blank screen). The ratio here used to be computed as
  // last_price_converted / best_value_price unconditionally — correct
  // ONLY for a best-value-preference pin, where last_price genuinely
  // IS the best-value fare. For a direct-preference pin, last_price
  // (and last_price_converted) is set from deals["direct"] instead
  // (see price_checker.py's own flight_preference override, right
  // where current_price is computed) — so the ratio was silently
  // dividing a DIRECT fare's converted value by a BEST-VALUE fare's
  // raw value, two different tickets' prices, whenever those two
  // fares' actual cost differed (the normal case). The result was an
  // arbitrary, wrong ratio: applied to direct_price via toComparable
  // below, it could push a genuinely-affordable direct flight's
  // comparable price above pin.budget, filtering it out of `deals`
  // entirely — and with badge:true living ONLY on the direct entry
  // for this pin, bestDeal then had nothing to find.
  //
  // Fix: last_price and last_price_converted are always the SAME
  // fare (whichever one flight_preference points to — see
  // Dashboard.js's comparablePrice(), which already relies on exactly
  // this fact). Using that pair, instead of mixing in the separate
  // best_value_price field, keeps the ratio meaningful regardless of
  // which fare type this pin is actually tracking.
  const priceCurrencyMismatch = pin.last_price_currency && pin.last_price_currency !== pin.currency;
  const fxRatio = (priceCurrencyMismatch && pin.last_price && pin.last_price_converted)
    ? pin.last_price_converted / pin.last_price
    : 1;
  const toComparable = (rawPrice) => rawPrice != null ? rawPrice * fxRatio : rawPrice;
  const rawBestValue = pin.best_value_price || pin.last_price;

  const deals = [
    {
      type: "Direct Flight",
      price: toComparable(pin.direct_price),
      rawPrice: pin.direct_price,
      description: "Fastest — no layovers",
      available: !!pin.direct_price,
      flight: pin.direct_flight,
      returnFlight: pin.direct_flight_return,
      backup: pin.direct_flight_backup,
      badge: isDirectPreference
    },
    {
      type: "With Stops",
      price: toComparable(pin.stops_price),
      rawPrice: pin.stops_price,
      description: "Cheapest — longer travel time",
      available: !!pin.stops_price,
      flight: pin.stops_flight,
      returnFlight: pin.stops_flight_return,
      backup: pin.stops_flight_backup,
      badge: false
    },
    {
      type: "Best Value",
      price: toComparable(rawBestValue),
      rawPrice: rawBestValue,
      description: "Best balance of price and time",
      available: true,
      flight: pin.best_value_flight,
      returnFlight: pin.best_value_flight_return,
      backup: pin.best_value_flight_backup,
      badge: !isDirectPreference
    }
  ].filter(d => d.available && d.price <= pin.budget);

  const bestDeal = deals.find(d => d.badge);

  // Crash guard (Sep 23, per Ali's report) — this page used to assume
  // bestDeal always exists once you can reach it at all (Dashboard
  // only links here when pinIsDeal(pin) is true), and dereferenced
  // bestDeal.price/.flight/etc unguarded throughout the render below.
  // The fxRatio bug above was ONE way to make deals.find(d => d.badge)
  // come back empty despite Dashboard showing a real deal — but this
  // guard is the actual fix for the crash itself: whatever the cause,
  // a real pin with no fare currently open for its preference should
  // show a plain, honest message, never a blank screen.
  if (!bestDeal) {
    const prefLabel = isDirectPreference ? 'a direct flight' : 'a fare';
    return (
      <div style={{ minHeight: '100vh', paddingBottom: '40px' }}>
        <PageBackground variant="calm" />
        <div style={{ background: theme.structural, color: theme.cream, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div
            onClick={() => navigate('/dashboard')}
            style={{ fontFamily: theme.fontDisplay, fontSize: '20px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
          >
            FlyAtlast
            <span aria-hidden="true" style={{ width: '7px', height: '7px', borderRadius: '50%', background: theme.action, display: 'inline-block' }} />
          </div>
        </div>
        <div style={{ maxWidth: '460px', margin: '60px auto', padding: '0 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '15px', fontWeight: 800, color: theme.structural, marginBottom: '10px' }}>
            {pin.destination}
          </div>
          <p style={{ fontSize: '14px', color: theme.textMuted, lineHeight: 1.6, marginBottom: '24px' }}>
            We don't currently have {prefLabel} within budget for this trip — the price you saw on your dashboard may have just changed, or this pin's preference was recently edited and hasn't been rechecked yet. We'll keep checking and let you know.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            style={{ background: theme.action, color: '#1a1a1a', border: 'none', padding: '12px 24px', fontWeight: 700, fontFamily: theme.fontDisplay, cursor: 'pointer' }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }
  // isSameFlight/secondaryDeals/distinctBackup all removed (Aug 13) —
  // their only consumer was the "More ways to book" section, itself
  // removed per Ali's explicit call (this sidebar isn't a monetized
  // surface and the collapsed backup/other-fare-type section was real
  // clutter nobody was asking to see). Nothing else in this file reads
  // any of the three.

  // Feeds the StatsCarousel at the bottom of the Deal page — every
  // slide comes from data this pin already carries (check_count,
  // price_history, the same freshness/isLowestYet computed above for
  // the hero), so a slide is simply skipped when its source data isn't
  // there yet rather than showing a placeholder number.
  const buildDealStatsSlides = () => {
    const slides = [];

    if ((pin.check_count || 0) > 0) {
      slides.push({
        icon: '🔎',
        value: pin.check_count,
        label: `price check${pin.check_count !== 1 ? 's' : ''} run on this trip`
      });
    }

    if (isLowestYet) {
      slides.push({
        icon: '★',
        value: 'Lowest yet',
        label: 'the best price seen on this trip so far',
        color: theme.success
      });
    }

    slides.push({
      icon: freshness.warn ? '⚠' : '🟢',
      value: freshness.warn ? 'Verify before booking' : 'Fresh',
      label: freshness.label
    });

    if (priceHistory.length >= 2) {
      slides.push({
        icon: '📈',
        value: priceHistory.length,
        label: 'price checks tracked for this trip'
      });
    }

    return slides;
  };

  // bookNowSection — extracted (Aug 14, per Ali's report) so the exact
  // same Book Now / booking-links / taxes-text JSX can be reused in two
  // different page positions (directly under the itinerary column when
  // a pin has no active freeze, or on its own in the simplified center
  // flow when one does) without duplicating ~100 lines of JSX and
  // risking the two copies drifting out of sync — same lesson as
  // ItineraryLeg.js's extraction after the earlier formatTime
  // production incident.
  const bookNowSection = (
    <>
              {!showBookingLinks && (
                <div className="guide-chevron" style={{ margin: '14px 0 6px', color: theme.textMuted }} aria-hidden="true">▾</div>
              )}

              {!showBookingLinks && (
                <>
                  <button
                    onClick={() => handleVerifyAndBook(bestDeal)}
                    disabled={verifyingPrice}
                    style={{
                      /* CTA hierarchy fix (Aug 16, per Ali's report):
                         Book Now is this page's actual highest-intent
                         action — the direct fulfillment of "at last,
                         book it" — so it gets theme.action (orange),
                         the one color reserved across the whole app for
                         whichever single button matters most on a given
                         screen. It used to be theme.structural (navy),
                         the same color used for secondary/structural UI
                         everywhere else, while Freeze this price (a
                         genuinely secondary, "not ready yet" path) was
                         the one in orange — backwards from what each
                         button actually represents. See FreezeChecklist
                         below for the matching demotion. */
                      width: '100%', maxWidth: '340px', background: theme.action, color: '#1a1a1a',
                      border: 'none', borderRadius: '14px', padding: '16px', fontWeight: 800, fontSize: '16px',
                      cursor: verifyingPrice ? 'default' : 'pointer', fontFamily: theme.fontDisplay,
                      opacity: verifyingPrice ? 0.7 : 1
                    }}
                  >
                    {verifyingPrice ? 'Checking current price...' : 'Book Now ✓'}
                  </button>
                  <p style={{ fontSize: '12px', color: theme.textMuted, marginTop: '8px' }}>
                    Verifies this price, then takes you to Skyscanner or Booking.com to book — prices may vary slightly there.
                  </p>
                </>
              )}
              {showBookingLinks && (
                <div style={{ width: '100%', maxWidth: '340px', margin: '0 auto', textAlign: 'center' }}>
                  {priceCheck?.status === 'verified' && (() => {
                    // current_price_converted comes from the backend fix
                    // above — only present when Duffel's real currency
                    // differs from the pin's budget currency. Falls back
                    // to the raw currency/price when they already match,
                    // same "converted primary, raw secondary" pattern as
                    // the hero price and Dashboard cards.
                    const displayCurrency = priceCheck.current_price_converted != null ? priceCheck.budget_currency : priceCheck.currency;
                    const displayCurrent = priceCheck.current_price_converted != null ? priceCheck.current_price_converted.toFixed(2) : priceCheck.current_price;
                    return (
                      <div style={{
                        background: priceCheck.direction === 'down' ? '#DCE6F1' : (priceCheck.direction === 'up' ? '#FCEBB0' : theme.cardBg),
                        border: `1px solid ${priceCheck.direction === 'down' ? theme.success : (priceCheck.direction === 'up' ? '#F4C11C' : theme.borderColor || '#ddd')}`,
                        borderRadius: '14px', padding: '12px', marginBottom: '14px', fontSize: '13px'
                      }}>
                        {priceCheck.direction === 'same' && (
                          <span>✓ Confirmed just now — still <strong>{displayCurrent} {displayCurrency}</strong> with our source.</span>
                        )}
                        {priceCheck.direction === 'down' && (
                          <span>Even better — just found <strong>{displayCurrent} {displayCurrency}</strong>.</span>
                        )}
                        {priceCheck.direction === 'up' && (
                          <span>Heads up — this price moved to <strong>{displayCurrent} {displayCurrency}</strong> since we last checked. Prices are dynamic and can change between checks.</span>
                        )}
                      </div>
                    );
                  })()}
                  {priceCheck?.status === 'unverified' && (() => {
                    // No fresh price came back from this attempt, so
                    // there's nothing new to convert — reuse the pin's
                    // already-known converted price (computed by the
                    // periodic price_checker job) instead of showing the
                    // raw previous_price in whatever currency Duffel
                    // last returned it in.
                    const hasConversion = pin.last_price_currency && pin.last_price_currency !== (pin.currency || 'CAD') && pin.last_price_converted != null;
                    const displayCurrency = hasConversion ? (pin.currency || 'CAD') : (pin.last_price_currency || pin.currency || 'CAD');
                    const displayPrice = hasConversion ? pin.last_price_converted.toFixed(2) : priceCheck.previous_price;
                    return (
                      <div style={{
                        background: theme.cardBg, border: `1px solid ${theme.borderColor || '#ddd'}`,
                        borderRadius: '14px', padding: '12px', marginBottom: '14px', fontSize: '13px', color: theme.textMuted
                      }}>
                        Couldn't re-check the live price just now — showing our last-checked price of <strong>{displayPrice} {displayCurrency}</strong>. Prices are dynamic and subject to change, so confirm on the site you book with.
                      </div>
                    );
                  })()}

                  <p style={{ fontSize: '12px', color: theme.textMuted, marginBottom: '10px', textAlign: 'center' }}>
                    Pick whichever shows the best match — prices may vary slightly across sites.
                  </p>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => window.open(buildSkyscannerUrl(), '_blank')}
                      style={{
                        flex: 1, background: '#0770E3', color: '#fff',
                        border: 'none', borderRadius: '14px', padding: '14px 10px', fontWeight: 700, fontSize: '14px',
                        cursor: 'pointer', fontFamily: theme.fontDisplay
                      }}
                    >
                      Skyscanner →
                    </button>
                    <button
                      onClick={() => window.open(buildBookingComUrl(), '_blank')}
                      style={{
                        flex: 1, background: '#003580', color: '#fff',
                        border: 'none', borderRadius: '14px', padding: '14px 10px', fontWeight: 700, fontSize: '14px',
                        cursor: 'pointer', fontFamily: theme.fontDisplay
                      }}
                    >
                      Booking.com →
                    </button>
                  </div>
                </div>
              )}

              <p style={{ fontSize: '12px', color: theme.textMuted, marginTop: '10px' }}>
                Includes taxes and fees. Final price and any extras confirmed when you book.
              </p>
    </>
  );


  return (
    <div style={{ minHeight: '100vh', paddingBottom: '40px' }}>
      <PageBackground variant="calm" />
      {/* Shared nav bar, identical to Dashboard */}
      <AppNav current="deal" />

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '28px 20px' }}>

        {deals.length === 0 && (
          <div style={{ textAlign: 'center', padding: '24px', background: theme.surface, borderRadius: '14px', border: `1px solid ${theme.border}`, maxWidth: '480px', margin: '0 auto' }}>
            <p style={{ color: theme.textMuted, marginBottom: '16px' }}>Prices may have changed since last check.</p>
            <button
              onClick={() => navigate('/dashboard')}
              style={{ background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '14px', padding: '12px 24px', fontWeight: 800, cursor: 'pointer', fontFamily: theme.fontDisplay }}
            >
              Back to Dashboard
            </button>
          </div>
        )}

        {bestDeal && (
          <div style={{ maxWidth: '1180px', margin: '0 auto' }}>

            {/* Section labels (Aug 17, per Ali's report) — same thin-
                line convention now used consistently across Dashboard
                and the Price Freeze page, applied here for the first
                time. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 14px' }}>
              <span style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#4A5F78', whiteSpace: 'nowrap' }}>Your Deal</span>
              <span style={{ flex: 1, height: '1px', background: theme.border }} />
            </div>

            {/* Destination photo banner (Aug 16, per Ali's report —
                design item 4 UI wiring). Additive, not a redesign of
                the hero below: this component's existing hero uses a
                compact circular icon + centered text, not a natural
                full-width area for a photo — retrofitting the hero
                itself was a bigger, riskier change than this pass
                covers, so this is a separate banner sitting above it
                instead. Renders nothing at all when no photo is
                available (still loading, Pexels not configured, or no
                results for this destination) — the existing hero looks
                and behaves exactly as it always has in that case. */}
            {heroPhotoUrl && (
              <div style={{
                // Height increased 140px -> 190px (Sep 16, per Ali's
                // report, paired with the landscape-size fix in
                // photo_service.py) — this container's width vastly
                // outscaled 140px tall, an extreme ~12:1 ratio far
                // wider than any normal photo's shape, forcing a heavy
                // crop regardless of source image quality. Narrower
                // gap now between container and photo shape means less
                // guessing left for background-size: cover to do.
                height: '190px', borderRadius: '14px', marginBottom: '18px', overflow: 'hidden',
                backgroundImage: `linear-gradient(180deg, rgba(31,50,69,0) 40%, rgba(31,50,69,0.55) 100%), url(${heroPhotoUrl})`,
                backgroundSize: 'cover', backgroundPosition: 'center'
              }} />
            )}

            {/* Hero — full rebuild (Aug 18, per Ali's report: the
                earlier horizontal-perforation version was a deliberate
                but overly cautious compromise on my part to avoid
                touching this section's dense currency/conversion
                logic — it didn't actually match what was approved,
                which is a real side-by-side ticket+stub like
                Dashboard's cards, not a stacked layout with a
                horizontal divider. Rebuilt properly this time: every
                computed value below (displayPrice, hasConversion,
                convertedDiff, historical stats) is the exact same
                logic as before, just relocated into the stub instead
                of staying centered under the identity content. The
                ribbon changes from an absolutely-positioned diagonal
                cut to a simple inline badge, matching the approved
                reference's "NEW LOW" badge style — still uses
                ribbon.color/textColor exactly as before, so different
                ribbon types (Rare Find, So Close, Lowest Ever) keep
                their own real meaning, just presented as a badge
                instead of a ribbon shape. Secondary details that don't
                fit the stub's minimal footprint (conversion sub-note,
                currency-mismatch warning, historical comparison) move
                to a footnote below the whole ticket rather than being
                cut — nothing here was deleted, only relocated. */}
            {(() => {
              const priceCurrency = pin.last_price_currency || pin.currency || 'CAD';
              const budgetCurrency = pin.currency || 'CAD';
              const currencyMismatch = priceCurrency !== budgetCurrency;
              const hasConversion = currencyMismatch && pin.last_price_converted != null;
              const displayPrice = hasConversion ? pin.last_price_converted : bestDeal.price;
              const displayCurrency = hasConversion ? budgetCurrency : priceCurrency;
              // Suppressed when this pin has an active freeze (Sep 18,
              // per Ali's report — item 10 of the QA doc: once a price
              // is frozen, it's already protected regardless of what
              // the market does next, so "Rare find" or "Price is
              // heating up" about a price that no longer affects the
              // real outcome is just noise. Same fix as the Dashboard
              // card ribbon and the rotating insight banner, all three
              // now agree.
              const ribbon = activeFreeze ? null : pickRibbon(pin);
              const showRibbon = ribbon && !freshness.warn;

              const convertedDiff = hasConversion ? pin.budget - pin.last_price_converted : null;
              const convertedIsUnder = convertedDiff != null && convertedDiff >= 0;
              const plainDiff = !currencyMismatch ? pin.budget - bestDeal.price : null;
              const plainIsUnder = plainDiff != null && plainDiff >= 0;

              const histStats = computeRoutePriceStats(pricePoints);
              const histCurrentPrice = priceCurrency === budgetCurrency ? bestDeal.price : pin.last_price_converted;
              const histPctDiff = (histStats && histStats.count >= 5 && histCurrentPrice != null)
                ? ((histStats.typical - histCurrentPrice) / histStats.typical) * 100 : null;
              const showHist = histPctDiff != null && Math.abs(histPctDiff) >= 2;

              return (
                <>
                  <div className="ticket-row" style={{ border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', background: theme.surface, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(30,58,95,0.07)' }}>
                    <div style={{ flex: 1, minWidth: 0, padding: '24px' }}>
                      {showRibbon && (
                        <div style={{
                          display: 'inline-block', background: ribbon.color, color: ribbon.textColor || '#ffffff',
                          fontSize: '10px', fontWeight: 800, padding: '4px 10px', letterSpacing: '0.04em',
                          textTransform: 'uppercase', marginBottom: '10px', fontFamily: theme.fontDisplay
                        }}>
                          {ribbon.icon} {ribbon.label}
                        </div>
                      )}
                      <h1 style={{ fontFamily: theme.fontDisplay, fontSize: '28px', fontWeight: 800, color: theme.textPrimary, margin: 0, lineHeight: 1.15 }}>
                        At last! {pinCity(pin)} is a deal
                      </h1>
                      {showInactiveNotice && (
                        <div style={{ marginTop: '10px', background: theme.bg, border: `1px solid ${theme.divider}`, padding: '10px 12px' }}>
                          <p style={{ fontSize: '12.5px', color: theme.textPrimary, margin: 0 }}>
                            {pin.lifetime_cap_reached
                              ? "This trip's included checks are used up — this is the last price we saw. Pin it again to keep tracking."
                              : "This trip isn't being tracked right now — this is the last price we saw."}
                          </p>
                          {!pin.lifetime_cap_reached && (
                            <button
                              onClick={handleReactivateFromDeal}
                              disabled={reactivating}
                              style={{ marginTop: '8px', fontSize: '12px', padding: '6px 12px', background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '14px', cursor: reactivating ? 'default' : 'pointer', fontWeight: 700, opacity: reactivating ? 0.6 : 1 }}
                            >
                              {reactivating ? 'Reactivating…' : '↻ Reactivate'}
                            </button>
                          )}
                          {reactivateError && (
                            <p style={{ fontSize: '12.5px', color: '#9c2f2f', marginTop: '6px', marginBottom: 0 }}>{reactivateError}</p>
                          )}
                        </div>
                      )}
                      {justReactivated && (
                        <p style={{ fontSize: '12.5px', color: theme.success, marginTop: '10px' }}>Reactivated — we'll start checking this trip again.</p>
                      )}
                      {pin.origin_code && pin.destination_code && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '18px' }}>
                          <span style={{ fontFamily: theme.fontMono, fontWeight: 700, fontSize: '20px', color: theme.textPrimary }}>{pin.origin_code}</span>
                          <span style={{ color: theme.divider }}>&#9992;</span>
                          <span style={{ fontFamily: theme.fontMono, fontWeight: 700, fontSize: '20px', color: theme.textPrimary }}>{pin.destination_code}</span>
                        </div>
                      )}
                      {(pin.departure_date || pin.travel_month) && (
                        <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '4px' }}>
                          {pin.matched_departure_date || pin.departure_date || pin.travel_month}
                          {(pin.matched_return_date || pin.return_date) ? ` – ${pin.matched_return_date || pin.return_date}` : ''}
                        </div>
                      )}
                      {/* Trip facts folded in from the old Trip Details bar (Oct 10,
                          per Ali's approved mockup), then the share row. */}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                        {(() => {
                          const adultsCount = pin.adults || 1;
                          const childAges = pin.child_ages || [];
                          const parts = [`${adultsCount} adult${adultsCount !== 1 ? 's' : ''}`];
                          if (childAges.length > 0) parts.push(`${childAges.length} child${childAges.length !== 1 ? 'ren' : ''} (age${childAges.length !== 1 ? 's' : ''} ${childAges.join(', ')})`);
                          const tag = { fontFamily: theme.fontMono, fontSize: '11.5px', fontWeight: 700, background: '#EAF1F8', color: theme.structural, padding: '5px 10px', borderRadius: '999px' };
                          return (
                            <>
                              <span style={tag}>{parts.join(', ')}</span>
                              {pin.budget != null && <span style={tag}>Budget {pin.budget} {pin.currency || 'CAD'}</span>}
                            </>
                          );
                        })()}
                      </div>
                      {pin.date_flexible && pin.matched_departure_date && pin.matched_departure_date !== pin.departure_date && (
                        <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '8px', lineHeight: 1.5 }}>
                          You searched <strong>{pin.departure_date}{pin.return_date ? ` → ${pin.return_date}` : ''}</strong> (flexible ±3 days). This price matched <strong>{pin.matched_departure_date}{pin.matched_return_date ? ` → ${pin.matched_return_date}` : ''}</strong>.
                        </div>
                      )}
                      {pin.id && (
                        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #EEF1F3' }}>
                          <div style={{ fontFamily: theme.fontMono, fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#4A5F78', marginBottom: '10px' }}>Take it with you</div>
                          <ShareDeal pinId={pin.id} variant="buttons" />
                        </div>
                      )}
                    </div>

                    <div className="ticket-stub" style={{ width: '300px', background: theme.action, padding: '22px 24px' }}>
                      {/* Price freshness (Oct 10, per Ali's approved Option 2) —
                          the check time and date sit right on the price. The dot
                          pulses ONLY when the check is under 15 minutes old
                          (freshness.live); an older price gets a still dot and
                          an honest "price may have moved" note, never a pulse
                          that would imply it is live. */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', fontFamily: theme.fontMono, fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#1E3A5F' }}>
                        <span style={{ position: 'relative', display: 'inline-block', width: '9px', height: '9px', flexShrink: 0 }}>
                          {freshness.live && <span className="pulse-ring" style={{ borderColor: '#1E3A5F' }} />}
                          <span style={{ position: 'absolute', inset: '1px', background: '#1E3A5F', borderRadius: '50%', opacity: freshness.live ? 1 : 0.45 }} />
                        </span>
                        {freshness.live ? 'Live price' : 'Last checked'}
                      </div>
                      <div style={{ fontFamily: theme.fontMono, fontWeight: 700, fontSize: '34px', color: '#1a1a1a', lineHeight: 1.1, marginTop: '4px' }}>
                        {formatMoney(displayPrice)}
                      </div>
                      <div style={{ fontSize: '13px', color: 'rgba(26,26,26,0.65)', fontWeight: 700 }}>{displayCurrency}</div>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: '#1E3A5F', marginTop: '6px', lineHeight: 1.4 }}>
                        {freshness.label.replace(/^Checked /, 'As of ')}
                      </div>

                      {hasConversion && (
                        convertedIsUnder ? (
                          <div style={{ marginTop: '12px', background: '#fff', borderRadius: '99px', display: 'inline-block', padding: '6px 12px', fontSize: '12px', fontWeight: 800, color: '#17663A' }}>
                            &#10003; ≈{convertedDiff.toFixed(0)} {budgetCurrency} under budget
                          </div>
                        ) : (
                          <div style={{ marginTop: '12px', background: '#fff', borderRadius: '99px', display: 'inline-block', padding: '6px 12px', fontSize: '12px', fontWeight: 800, color: '#993C1D' }}>
                            ≈{Math.abs(convertedDiff).toFixed(0)} {budgetCurrency} over budget
                          </div>
                        )
                      )}
                      {!currencyMismatch && (
                        plainIsUnder ? (
                          <div style={{ marginTop: '12px', background: '#fff', borderRadius: '99px', display: 'inline-block', padding: '6px 12px', fontSize: '12px', fontWeight: 800, color: '#17663A' }}>
                            &#10003; {plainDiff.toFixed(0)} {budgetCurrency} under budget
                          </div>
                        ) : (
                          <div style={{ marginTop: '12px', background: '#fff', borderRadius: '99px', display: 'inline-block', padding: '6px 12px', fontSize: '12px', fontWeight: 800, color: '#993C1D' }}>
                            {Math.abs(plainDiff).toFixed(0)} {budgetCurrency} over budget
                          </div>
                        )
                      )}
                      {currencyMismatch && !hasConversion && (
                        <div style={{ marginTop: '14px', background: 'rgba(26,26,26,0.12)', padding: '8px 10px', fontSize: '12.5px', fontWeight: 700, color: '#993C1D' }}>
                          ⚠ Priced in {priceCurrency}, budget in {budgetCurrency} — couldn't convert
                        </div>
                      )}
                      {/* Moved inside the deal card itself (Sep 8, per
                          Ali's report) — previously sat outside both
                          this card and the Trip Details bar below it,
                          floating alone in the page background, which
                          read as orphaned clutter rather than part of
                          the deal it was actually explaining. Still
                          right next to the fare/budget figures above,
                          just properly contained now. Wording on the
                          conversion line tightened too — it named a
                          USD figure with nothing tying it to the EUR
                          fare above it; now explicit that USD is the
                          original price and EUR is the converted one.
                          The two lines get their own visual separation
                          (a light top rule + more breathing room)
                          rather than sitting stacked as one paragraph
                          — they're doing different jobs: one is a
                          booking-accuracy caution, the other is a
                          reason this is genuinely a good price, and
                          they shouldn't read as the same kind of note. */}
                      {!showBookingLinks && (
                        <button
                          onClick={async () => {
                            await handleVerifyAndBook(bestDeal);
                            const el = document.getElementById('deal-book-section');
                            if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }}
                          disabled={verifyingPrice}
                          style={{
                            display: 'block', width: '100%', marginTop: '14px', background: '#1E3A5F', color: '#fff', border: 'none',
                            borderRadius: '12px', padding: '14px', fontWeight: 800, fontSize: '15px', fontFamily: theme.fontDisplay,
                            cursor: verifyingPrice ? 'default' : 'pointer', opacity: verifyingPrice ? 0.7 : 1
                          }}
                        >
                          {verifyingPrice ? 'Checking current price...' : 'Book now ✓'}
                        </button>
                      )}
                      {!showBookingLinks && (
                        <p style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.4, margin: '8px 0 0', textAlign: 'center' }}>
                          Verifies this price, then opens Skyscanner or Booking.com. Prices may vary slightly there.
                        </p>
                      )}
                      {((currencyMismatch && !hasConversion) || showHist) && (
                        <div style={{ borderTop: '1px solid rgba(30,58,95,0.25)', marginTop: '16px', paddingTop: '12px' }}>
                          {currencyMismatch && !hasConversion && (
                            <div style={{ fontSize: '12px', color: theme.textMuted, lineHeight: 1.5 }}>
                              Check the actual amount before booking.
                            </div>
                          )}
                          {showHist && (
                            <div style={{ fontSize: '13px', color: theme.structural, fontWeight: 700, lineHeight: 1.5, marginTop: (currencyMismatch && !hasConversion) ? '8px' : 0 }}>
                              Usually around {formatMoney(histStats.typical)} {budgetCurrency} for this route — today it's {formatMoney(histCurrentPrice)}.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Alternative fare (Oct 2026): one other fare, with its itinerary,
                and a one-time switch. Hidden once a Price Watch exists or has
                existed, after a switch, and when the trip can't be switched. */}
            {(() => {
              const alt = getAltFare(pin, fxRatio);
              if (!alt || !freezesLoaded || activeFreeze || resolvedFreeze || !canSwitchFare(pin, hasFreezeHistory)) return null;
              const cheaper = alt.diff < -0.5;
              const dearer = alt.diff > 0.5;
              const pill = alt.noTrackedFare ? 'Only option found' : cheaper ? `${formatMoney(Math.abs(alt.diff))} less` : dearer ? `${formatMoney(alt.diff)} more` : 'Same price';
              return (
                <div style={{ margin: '22px 0 0', border: `2px solid ${theme.border}`, background: theme.surface, padding: '14px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ minWidth: '160px' }}>
                      <div style={{ fontFamily: theme.fontMono, fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#4A5F78' }}>Another option</div>
                      <div style={{ fontFamily: theme.fontDisplay, fontWeight: 700, fontSize: '16px', color: theme.textPrimary, marginTop: '2px' }}>{altTitle(alt)}</div>
                    </div>
                    <div style={{ textAlign: 'right', marginLeft: 'auto' }}>
                      <div style={{ fontFamily: theme.fontDisplay, fontWeight: 800, fontSize: '20px', color: theme.structural }}>{formatMoney(alt.price)} {pin.currency}</div>
                      <span style={{ fontSize: '11px', fontWeight: 800, padding: '2px 8px', background: cheaper ? '#E3F4E8' : theme.bg, color: cheaper ? '#1B6B3A' : theme.textMuted }}>{pill}</span>
                      <div>
                        <button onClick={() => setAltOpen(o => !o)} style={{ marginTop: '10px', padding: '7px 14px', background: theme.surface, color: theme.structural, border: `2px solid ${theme.structural}`, borderRadius: '0px', fontWeight: 800, fontSize: '12.5px', cursor: 'pointer' }}>
                          {altOpen ? 'Hide itinerary ▴' : 'See itinerary ▾'}
                        </button>
                      </div>
                    </div>
                  </div>
                  {altOpen && (
                    <div style={{ marginTop: '10px' }}>
                      <ItineraryLeg label={alt.returnFlight ? 'Outbound' : 'Your flight'} flight={alt.flight} />
                      {alt.returnFlight && <ItineraryLeg label="Return" flight={alt.returnFlight} />}
                    </div>
                  )}
                  <button onClick={() => setSwitchOpen(true)} style={{ width: '100%', marginTop: '12px', padding: '11px', background: theme.action, color: theme.structural, border: `2px solid ${theme.structural}`, borderRadius: '0px', fontWeight: 800, fontSize: '14px', cursor: 'pointer' }}>
                    Track this fare instead
                  </button>
                  <div style={{ fontSize: '11.5px', color: theme.textMuted, lineHeight: 1.5, marginTop: '8px' }}>
                    You can switch the fare this trip tracks once. Switching doesn't start a Price Watch.
                  </div>
                  {switchOpen && (
                    <SwitchFareModal
                      pin={pin}
                      alt={alt}
                      currency={pin.currency}
                      formatMoney={formatMoney}
                      onClose={() => setSwitchOpen(false)}
                      onDone={() => navigate('/dashboard')}
                    />
                  )}
                </div>
              );
            })()}

            {activeFreeze ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '28px 0 14px' }}>
                  <span style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#4A5F78', whiteSpace: 'nowrap' }}>Book This Trip</span>
                  <span style={{ flex: 1, height: '1px', background: theme.border }} />
                </div>
                <div style={{ maxWidth: '340px', margin: '0 auto', textAlign: 'center' }}>
                  <div id="deal-book-section" />
                  {bookNowSection}
                </div>
              </>
            ) : (
              <>
                {/* One combined label, not two separate ones (Aug 17,
                    per Ali's report) — these two cards are a deliberate
                    side-by-side pairing meant to be compared at once
                    ("book now, or wait and protect the price instead"),
                    not two unrelated sequential sections. Splitting
                    them with separate labels would suggest they're
                    independent steps rather than the real choice being
                    presented together. */}
                <SectionLabel style={{ margin: '28px 0 14px' }}>Your itinerary</SectionLabel>
                <div className="deal-itin-freeze-row">
              <div style={{ textAlign: 'center', border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '14px', padding: '18px', alignSelf: itinOpen ? 'stretch' : 'start' }}>
                {/* Itinerary folded behind a big yellow button, same as the
                    Price Watch page (Oct 10, per Ali). The single Book now
                    lives in the yellow price block above; the booking links
                    appear here, outside the fold, once it is pressed. */}
                {bestDeal.flight && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', flexWrap: 'wrap', textAlign: 'left', marginBottom: itinOpen ? '16px' : '4px' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '16px', fontWeight: 800, color: theme.structural }}>Flights and price history</div>
                      <div style={{ fontSize: '12.5px', color: theme.textMuted, marginTop: '3px' }}>
                        Flights and Google Flights link. {formatMoney(bestDeal.price)} {pin.currency || 'CAD'} total.
                      </div>
                    </div>
                    <button
                      onClick={() => setItinOpen(v => !v)}
                      style={{ background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '12px', padding: '13px 24px', fontWeight: 800, fontSize: '14.5px', cursor: 'pointer', fontFamily: theme.fontDisplay, whiteSpace: 'nowrap' }}
                    >
                      {itinOpen ? 'Hide itinerary ▴' : 'See itinerary ▾'}
                    </button>
                  </div>
                )}
                {itinOpen && bestDeal.flight && (
                    <div style={{
                      textAlign: 'left', background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: '14px',
                      padding: '14px 16px', marginBottom: '18px'
                    }}>
                  {/* Price folded into the itinerary's own header row
                      (Sep 23, per Ali's report) rather than sitting as
                      a separate full-bleed banner above it — this is
                      the itinerary priced at this total, not two
                      unrelated facts stacked on top of each other. */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
                    <span style={{ fontSize: '10.5px', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Itinerary</span>
                    <span style={{ fontFamily: theme.fontMono, fontSize: '15px', fontWeight: 800, color: theme.structural }}>{formatMoney(bestDeal.price)} {pin.currency || 'CAD'}</span>
                  </div>

                  <ItineraryLeg label={bestDeal.returnFlight ? 'Outbound' : 'Your flight'} flight={bestDeal.flight} />
                  {bestDeal.returnFlight && (
                    <ItineraryLeg label="Return" flight={bestDeal.returnFlight} />
                  )}

                  <button
                    onClick={() => handleLookup(bestDeal.flight)}
                    style={{
                      width: '100%', marginTop: '4px', background: 'transparent', color: theme.structural,
                      border: `1px solid ${theme.structural}`, borderRadius: '14px', padding: '9px',
                      fontWeight: 700, fontSize: '12px', cursor: 'pointer', fontFamily: theme.fontDisplay
                    }}
                  >
                    Look up {bestDeal.flight.flight_number}
                  </button>

                  {/* Google Flights — added as a third real option, using
                      ONLY the plain origin/destination/dates query
                      format. Tested live: adding natural-language hints
                      (airline, time, "nonstop") to the query broke it
                      entirely, landing on a blank homepage instead of
                      results — so this deliberately stays minimal, the
                      one version confirmed to actually work. Does NOT
                      claim to land on the exact flight (nothing can,
                      including Skyscanner/Booking.com above) — the
                      caption is honest about what it's actually good
                      for: narrowing down manually once there. */}
                  {(() => {
                    const { departureDate, returnDate } = getLinkParams();
                    if (!pin.origin_code || !pin.destination_code || !departureDate) return null;
                    const fmtDate = (d) => {
                      const dt = new Date(d + 'T00:00:00');
                      return dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                    };
                    let query = `Flights from ${pin.origin_code} to ${pin.destination_code} on ${fmtDate(departureDate)}`;
                    if (returnDate) query += ` through ${fmtDate(returnDate)}`;
                    const gflightsUrl = `https://www.google.com/travel/flights?q=${encodeURIComponent(query)}`;
                    return (
                      <div style={{ marginTop: '8px' }}>
                        <a
                          href={gflightsUrl} target="_blank" rel="noopener noreferrer"
                          style={{
                            display: 'block', textAlign: 'center', background: '#fff', color: '#1E3A5F', border: '2px solid #1E3A5F',
                            padding: '10px', borderRadius: '12px', fontSize: '12.5px', fontWeight: 800, textDecoration: 'none'
                          }}
                        >
                          Find your exact flight on Google Flights →
                        </a>
                        <p style={{ fontSize: '11.5px', color: theme.textMuted, textAlign: 'center', marginTop: '5px' }}>
                          Once there, use the Times and Airlines filters to narrow down to this exact flight.
                        </p>
                      </div>
                    );
                  })()}

                    </div>
                  )}
                  <div id="deal-book-section" />
                  {showBookingLinks && <div style={{ marginTop: '16px' }}>{bookNowSection}</div>}
                </div>

                <div style={{ border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '14px', padding: '18px', display: 'flex', flexDirection: 'column' }}>
                  {/* Resolved-freeze banner (Sep 4, per Ali's report) —
                      same treatment and color convention as the
                      Dashboard card badge: kept = navy, refunded =
                      green. Only shows when there's no currently
                      active freeze (that has its own full section
                      further down this page already). */}
                  {resolvedFreeze && (
                    <div
                      onClick={() => navigate('/freeze', { state: { freezeId: resolvedFreeze.id, userId: pin.user_id } })}
                      style={{
                        background: OUTCOME_STYLE[watchOutcome(resolvedFreeze.status, resolvedFreeze.payment_status, resolvedFreeze.fee_amount)].bg,
                        color: OUTCOME_STYLE[watchOutcome(resolvedFreeze.status, resolvedFreeze.payment_status, resolvedFreeze.fee_amount)].fg, padding: '10px 14px', marginBottom: '14px', fontSize: '12.5px',
                        fontWeight: 600, cursor: 'pointer'
                      }}
                    >
                      Your last Price Watch on this trip: {resolvedFreeze.status === 'kept' ? 'the fee was charged' : 'you paid nothing'} — view it →
                    </div>
                  )}
                  {/* Simplified (Aug 31, per Ali's report — Phase 4):
                      this whole block already only renders inside the
                      outer `{bestDeal && (...)}` condition, which
                      structurally guarantees pin_is_deal is true here —
                      the only real eligibility rule now (routes/
                      freezes.py). No branching needed anymore; always
                      render the ready state directly.

                      Reopened (Sep 21, per Ali's decision on the
                      minimum-runway floor) — "only real eligibility
                      rule" stopped being true the moment
                      departure_needs_new_date and the runway floor
                      both became real, separate reasons create_freeze
                      can refuse a purchase (routes/freezes.py). This
                      component never gained either guard — only
                      Dashboard.js's own Freeze button did — so this
                      screen could show "Price Freeze is ready" and
                      invite a click the backend would then reject.
                      Mirrors Dashboard.js's own guard logic rather
                      than duplicating create_freeze's live
                      computation — pin already carries both fields
                      from the same get_pins() response Dashboard.js
                      read to get here. */}
                  {(() => {
                    // Inside the 48-hour proof window of a charged Price Watch (same rule the
                    // Dashboard's Submit Proof section uses), this panel offers proof, not a new watch.
                    const f = resolvedFreeze;
                    if (!f || f.status !== 'kept' || f.is_trial || !f.resolved_at || f.evidence_status) return null;
                    const hrs = (Date.now() - new Date(f.resolved_at).getTime()) / 3600000;
                    if (!(hrs >= 0 && hrs < 48)) return null;
                    return { f, left: Math.max(0, Math.ceil(48 - hrs)) };
                  })() ? (() => {
                    const f = resolvedFreeze;
                    const left = Math.max(0, Math.ceil(48 - (Date.now() - new Date(f.resolved_at).getTime()) / 3600000));
                    return (
                      <div style={{ background: theme.structural, borderRadius: '14px', padding: '20px', color: '#fff' }}>
                        <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '6px' }}>Your Price Watch has finished</div>
                        <div style={{ fontSize: '13px', opacity: 0.9, lineHeight: 1.55, marginBottom: '14px' }}>
                          Paid more than {f.frozen_price} {f.currency}? Send us your booking within {left > 0 ? `${left} hours` : 'the next few hours'} and we'll refund the fee.
                        </div>
                        <div
                          onClick={() => navigate('/freeze', { state: { freezeId: f.id, userId: pin.user_id } })}
                          style={{ background: theme.action, color: '#1a1a1a', textAlign: 'center', padding: '11px', fontWeight: 800, fontSize: '14px', cursor: 'pointer', border: `2px solid ${theme.action}` }}
                        >
                          Submit proof →
                        </div>
                      </div>
                    );
                  })() : pin.departure_needs_new_date || pin.freeze_runway_ok === false ? (
                    <div style={{ background: theme.structural, borderRadius: '14px', padding: '20px', color: '#fff' }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '6px' }}>Price Watch isn't available yet</div>
                      <div style={{ fontSize: '12.5px', opacity: 0.85, lineHeight: 1.5 }}>
                        {pin.departure_needs_new_date
                          ? "This trip's departure date needs to be updated before Price Watch can start — set a new date first."
                          : `Price Watch needs at least ${pin.freeze_min_runway_hours || 24} hours before departure to be worth it — this trip departs too soon.`}
                      </div>
                    </div>
                  ) : (
                    <FreezeChecklist
                      freezeStatus={freezeStatus}
                      freezeError={freezeError}
                      onFreeze={() => setShowDurationModal(true)}
                      pin={pin}
                      trialAvailable={trialAvailable}
                      trialStatus={trialStatus}
                      trialError={trialError}
                      onStartTrial={handleStartTrial}
                    />
                  )}
                </div>
              </div>
              <SectionLabel style={{ margin: '28px 0 14px' }}>Your price, tracked</SectionLabel>
              <div style={{ background: '#fff', border: '1px solid #DDE3E7', borderLeft: '6px solid #F4C11C', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', borderRadius: '14px', padding: '18px 22px' }}>
                <DealMiniChart points={pricePoints} checksLogged={pin.check_count} currency={pin.currency || 'CAD'} standalone />
              </div>
              </>
            )}
          </div>
        )}

        {bestDeal && activeFreeze && (
          <div style={{ maxWidth: '1180px', margin: '16px auto 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '14px 0 14px' }}>
              <span style={{ fontFamily: theme.fontMono, fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#4A5F78', whiteSpace: 'nowrap' }}>⏱ Your Price Watch</span>
              <span style={{ flex: 1, height: '1px', background: theme.border }} />
            </div>
            <div
              onClick={() => navigate('/freeze', { state: { freezeId: activeFreeze.id, userId: pin.user_id, fromDeal: true, pin } })}
              style={{
                background: theme.structural, borderRadius: '14px', padding: '20px 28px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer'
              }}
            >
              <div>
                <div style={{ fontSize: '15px', fontWeight: 800, color: '#fff' }}>
                  Price Watch · Starting price {activeFreeze.frozen_price} {activeFreeze.currency}
                </div>
                <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#B9CBE0', marginTop: '3px' }}>Checked every 2 hours</div>
              </div>
              <span style={{ fontSize: '12.5px', fontWeight: 800, color: '#fff', background: 'rgba(255,255,255,0.15)', padding: '11px 22px', borderRadius: '14px', whiteSpace: 'nowrap' }}>
                Track my watch →
              </span>
            </div>
          </div>
        )}

        {bestDeal && <TripExtras pin={pin} wide />}

        {/* Bottom "Back to Dashboard" button removed (Aug 17, per Ali's
            report) — same redundancy already found and fixed on the
            Price Freeze page: the nav bar's "← Back to Trips" link
            already covers this, so a second way to do the same thing
            was just visual clutter at the bottom of the page. */}

        {/* Duration picker step (Sep 8, per Ali's report) — the real
            first step of the two-step freeze flow: opens on clicking
            "Freeze this price," and picking a tier here is what
            actually opens FreezePaymentModal next. */}
        {showDurationModal && (
          <FreezeDurationModal
            pinId={pin.id}
            onChoose={handleDurationChosen}
            onCancel={() => setShowDurationModal(false)}
            departureStopTrackingAt={pin.departure_stop_tracking_at}
          />
        )}
        {showPaymentModal && (
          <FreezePaymentModal
            userId={pin.user_id}
            pin={pin}
            quote={selectedQuote}
            todaysPrice={bestDeal?.price}
            // Same effective-end logic as the duration modal's own
            // per-tier cutoff check and the backend's get_freezes —
            // the nominal duration, unless departure's 12h cutoff
            // comes first. Recomputed here rather than trusting a
            // value handed up from the duration step, since some time
            // has passed between picking a tier and confirming.
            resolvesAt={(() => {
              const nominal = Date.now() + selectedDurationHours * 3600000;
              const stopAt = pin.departure_stop_tracking_at ? new Date(pin.departure_stop_tracking_at).getTime() : null;
              return new Date(stopAt ? Math.min(nominal, stopAt) : nominal);
            })()}
            onConfirm={handleFreeze}
            onCancel={() => setShowPaymentModal(false)}
          />
        )}
        {pendingFreezeNav && (
          <PurchaseSuccessModal
            type="freeze"
            feeAmount={FREEZE_DURATION_TIERS.find(t => t.hours === selectedDurationHours)?.fee}
            durationLabel={FREEZE_DURATION_TIERS.find(t => t.hours === selectedDurationHours)?.label}
            onDismiss={() => navigate('/freeze', { state: pendingFreezeNav })}
          />
        )}
      </div>
    </div>
  );
}

export default DealScreen;